import { useRef } from 'react'
import { X } from 'lucide-react'
import { useDialogA11y, useStableId } from '@/hooks/useDialogA11y'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_HEADING, FONT_BODY } from '@/config/theme'

interface ModalProps {
  show: boolean
  onClose: () => void
  title: string
  closable?: boolean
  children: React.ReactNode
}

export function Modal({ show, onClose, title, closable = true, children }: ModalProps) {
  const c = useThemeColors()
  const cardRef = useRef<HTMLDivElement>(null)
  const overlayPressRef = useRef(false)
  const titleId = useStableId('modal-title')
  useDialogA11y(cardRef, show, onClose, closable)

  if (!show) return null

  const handleOverlayMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    overlayPressRef.current = e.target === e.currentTarget
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!closable) return
    if (e.target === e.currentTarget && overlayPressRef.current) {
      onClose()
    }
    overlayPressRef.current = false
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={{
        position: 'fixed', inset: 0, background: c.mo,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        alignItems: 'safe center',
        justifyContent: 'safe center',
        zIndex: 100,
        padding: 'max(24px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
        animation: 'fadeInOverlay 0.15s ease-out both',
      }}
      onMouseDown={handleOverlayMouseDown}
      onTouchStart={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        style={{
          background: c.card, borderRadius: 24, width: '100%', maxWidth: 440,
          border: `1px solid ${c.cb}`,
          flexShrink: 0,
          outline: 'none',
          animation: 'slideUp 0.25s ease-out both',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${c.dv}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span id={titleId} style={{ fontWeight: 600, color: c.tx, fontSize: 17, fontFamily: FONT_HEADING }}>
            {title}
          </span>
          {closable && (
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', color: c.mu, cursor: 'pointer',
                padding: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: FONT_BODY,
              }}
              aria-label="Close"
            >
              <X size={22} />
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  )
}
